import { createClient } from '@supabase/supabase-js';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './casino.css';

const emptyLeaderboards = {
  current: [],
  blackjack: [],
  roulette: [],
  slots: [],
  highestWallet: [],
};

export default function CasinoPage() {
  const pageRef = useRef(null);
  const gameFrameRef = useRef(null);
  const [showInfo, setShowInfo] = useState(false);
  const [authMode, setAuthMode] = useState('create');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const [authError, setAuthError] = useState('');
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [gameReloadKey, setGameReloadKey] = useState(0);
  const [showRules, setShowRules] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboards, setLeaderboards] = useState(emptyLeaderboards);
  const [leaderboardError, setLeaderboardError] = useState('');
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const supabase = useMemo(() => {
    if (!supabaseUrl || !supabaseAnonKey) {
      return null;
    }

    return createClient(supabaseUrl, supabaseAnonKey);
  }, [supabaseUrl, supabaseAnonKey]);
  const isAuthConfigured = Boolean(supabase);
  const reloadGame = useCallback(() => {
    setGameReloadKey((currentKey) => currentKey + 1);
  }, []);

  const resetCasinoScroll = useCallback(() => {
    window.scrollTo(0, 0);
  }, []);

  const holdCasinoScrollAtTop = useCallback(() => {
    resetCasinoScroll();

    const animationFrame = window.requestAnimationFrame(resetCasinoScroll);
    const shortDelay = window.setTimeout(resetCasinoScroll, 80);
    const bootDelay = window.setTimeout(resetCasinoScroll, 350);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(shortDelay);
      window.clearTimeout(bootDelay);
    };
  }, [resetCasinoScroll]);

  useLayoutEffect(() => {
    return holdCasinoScrollAtTop();
  }, [holdCasinoScrollAtTop]);

  const handleGameFrameLoad = () => {
    postCasinoSessionToGame();
    holdCasinoScrollAtTop();
  };


  const postCasinoSessionToGame = useCallback(() => {
    const gameWindow = gameFrameRef.current?.contentWindow;

    if (!gameWindow) {
      return;
    }

    gameWindow.postMessage(
      {
        type: 'casino-session',
        user_id: session?.user?.id || '',
        access_token: session?.access_token || '',
        wallet: profile?.wallet ?? null,
      },
      window.location.origin
    );
  }, [profile?.wallet, session?.access_token, session?.user?.id]);

  useEffect(() => {
    if (!supabase) {
      return undefined;
    }

    let isMounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (isMounted) {
        setSession(data.session);
      }
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [supabase]);

  useEffect(() => {
    postCasinoSessionToGame();
  }, [postCasinoSessionToGame]);

  useEffect(() => {
    const handleCasinoGameMessage = (event) => {
      if (event.origin !== window.location.origin || !event.data) {
        return;
      }

      if (event.data.type === 'casino-ready') {
        postCasinoSessionToGame();
        return;
      }

      if (event.data.type === 'casino-wallet-updated') {
        const nextWallet = Number(event.data.wallet);

        if (Number.isInteger(nextWallet)) {
          setProfile((currentProfile) => (
            currentProfile
              ? {
                  ...currentProfile,
                  wallet: nextWallet,
                }
              : currentProfile
          ));
        }
        return;
      }

      if (event.data.type === 'casino-result-error') {
        setAuthError(event.data.error || 'Could not save casino result.');
      }
    };

    window.addEventListener('message', handleCasinoGameMessage);

    return () => {
      window.removeEventListener('message', handleCasinoGameMessage);
    };
  }, [postCasinoSessionToGame]);

  useEffect(() => {
    if (!supabase || !session?.user?.id) {
      setProfile(null);
      return undefined;
    }

    let isMounted = true;

    supabase
      .from('profiles')
      .select('display_name, wallet')
      .eq('user_id', session.user.id)
      .single()
      .then(({ data, error }) => {
        if (!isMounted) {
          return;
        }

        if (error) {
          setProfile(null);
          return;
        }

        setProfile(data);
      });

    return () => {
      isMounted = false;
    };
  }, [session, supabase]);

  useEffect(() => {
    if (!showLeaderboard) {
      return undefined;
    }

    let isMounted = true;

    setLeaderboardError('');

    fetch('/.netlify/functions/leaderboards')
      .then(async (response) => {
        const body = await response.json();

        if (!response.ok) {
          throw new Error(body.error || 'Could not load leaderboard.');
        }

        return body;
      })
      .then((data) => {
        if (!isMounted) {
          return;
        }

        setLeaderboards({
          current: data.current || [],
          blackjack: data.blackjack || [],
          roulette: data.roulette || [],
          slots: data.slots || [],
          highestWallet: data.highestWallet || [],
        });
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        setLeaderboards(emptyLeaderboards);
        setLeaderboardError(error.message);
      });

    return () => {
      isMounted = false;
    };
  }, [profile?.wallet, showLeaderboard]);

  const resetAuthStatus = () => {
    setAuthMessage('');
    setAuthError('');
  };

  const handleAuthModeChange = (nextMode) => {
    setAuthMode(nextMode);
    resetAuthStatus();
  };

  const handleCreateAccount = async () => {
    if (!supabase) {
      setAuthError('Supabase is not configured yet.');
      return;
    }

    const trimmedDisplayName = displayName.trim();
    const trimmedEmail = email.trim();

    if (!trimmedDisplayName) {
      setAuthError('Choose a display name.');
      return;
    }

    if (!trimmedEmail) {
      setAuthError('Enter an email address.');
      return;
    }

    if (password.length < 6) {
      setAuthError('Use a password with at least 6 characters.');
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email: trimmedEmail,
      password,
      options: {
        data: {
          display_name: trimmedDisplayName,
        },
      },
    });

    if (error) {
      setAuthError(error.message);
      return;
    }

    if (!data.user) {
      setAuthError('Account creation did not return a user.');
      return;
    }

    if (!data.session) {
      setAuthMessage(
        'Account created, check email to confirm account.'
      );
      return;
    }

    setSession(data.session);
    setProfile({
      display_name: trimmedDisplayName,
      wallet: 5000,
    });
    reloadGame();
    setPassword('');
    setAuthMessage('Account created. Progress is now saving.');
  };

  const handleSignIn = async () => {
    if (!supabase) {
      setAuthError('Supabase is not configured yet.');
      return;
    }

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setAuthError('Enter an email address.');
      return;
    }

    if (!password) {
      setAuthError('Enter your password.');
      return;
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: trimmedEmail,
      password,
    });

    if (error) {
      setAuthError(error.message);
      return;
    }

    setSession(data.session);
    reloadGame();
    setPassword('');
    setAuthMessage('Signed in. Progress saving is ready.');
  };

  const handleAuthSubmit = async (event) => {
    event.preventDefault();
    resetAuthStatus();

    if (authMode === 'create') {
      await handleCreateAccount();
      return;
    }

    await handleSignIn();
  };

  const handleSignOut = async () => {
    resetAuthStatus();

    if (supabase) {
      const { error } = await supabase.auth.signOut();

      if (error) {
        setAuthError(error.message);
        return;
      }
    }

    setSession(null);
    setProfile(null);
    reloadGame();
  };

  const renderLeaderboard = (title, entries, valueKey, valuePrefix, valueSuffix) => (
    <div className="casino-page__leaderboard-board">
      <h3>{title}</h3>
      {entries.length > 0 ? (
        <ol className="casino-page__leaderboard-list">
          {entries.map((entry, index) => (
            <li className="casino-page__leaderboard-row" key={`${title}-${entry.display_name}-${index}`}>
              <span className="casino-page__leaderboard-rank">{index + 1}</span>
              <span className="casino-page__leaderboard-name">{entry.display_name}</span>
              <span className="casino-page__leaderboard-value">
                {valuePrefix}{Number(entry[valueKey]).toLocaleString()}{valueSuffix}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="casino-page__collapsible-intro">No entries yet.</p>
      )}
    </div>
  );

  return (
    <main className="casino-page" ref={pageRef}>
      <div className="lines" aria-hidden="true">
        {[...Array(10)].map((_, i) => (
          <div className="line" key={i}></div>
        ))}
      </div>
      <button
        type="button"
        className="casino-page__info-button"
        aria-expanded={showInfo}
        aria-controls="casino-page-tech-info"
        aria-label={showInfo ? 'Hide technical details' : 'Show technical details'}
        onClick={() => setShowInfo((current) => !current)}
      >
        i
      </button>
      {showInfo ? (
        <aside className="casino-page__info-panel" id="casino-page-tech-info">
          <h2>How the Account System Works</h2>
          <p>
            The account system is built using Supabase. When you create an account, your display name and wallet are stored in a Supabase database.
            Your wallet is updated whenever you play a game and the results are recorded in the database.
          </p>
          <p>
            The account system uses Supabase's authentication system to manage user sessions. 
            When you sign in, a session is created and stored in your browser's local storage.
            The session is used to authenticate requests to the Supabase database and to keep track of your progress.
          </p>
          <p>
            The game in Godot is passed the session information. After each game round, the game sends a message to the React app with the results. The React app then sends a request to a Netlify function which records the results in the Supabase database.
          </p>
          <p>
            When you sign out, the session is cleared from local storage and you will need to sign in again to access your account.
          </p>

          <h2>How the Game Was Built</h2>
          <p>
            This game was created using Godot as the game engine, C# for underlying code and pixellab.ai/codex/other open sources to create the 2D art.
          </p>
          <p>
            The casino layout was designed by me. I found tile maps online and placed the individual tiles to create the casino.
          </p>
          <p>
            The main character, bartender, game keepers and NPCs were created by pixellab.ai and codex which gave me animations and frames to use. 
            Through C# code, I was able to provide movement to the NPCs and allowed control of the main character. 
            The characters have colliders which prevented them from walking straight through other objects.
            The main character's collisions are tracked to be able to interact and play a game.
          </p>
          <p>
            Each game is designed by me using open source art I found online then converted to pngs.
            Each game's logic was coded in C# and then connected to Godot in order to display interactive games.
          </p>
        </aside>
      ) : null}
      <div className="casino-page__content">
        <section className="casino_page__intro">
          <h1>Welcome to Casino de Cray</h1>
          <p>
            Click the info button to see what technologies were used to build this game. 
          </p>
          <p>
            Game rules and controls are available under the game. 
          </p>
          <p>
            View leaderboard at the bottom and see how you rank against other players. 
          </p>
          <p>
            If you are on a mobile device, playing horizontally yields a larger screen. 
          </p>
        </section>
        <section className="casino-page__auth-panel" aria-label="Casino account sign in">
          <div className="casino-page__auth-copy">
            <h2>{session ? 'Progress is being tracked' : 'Save your casino progress'}</h2>
            <p>
              {session
                ? `Signed in as ${profile?.display_name || session.user.email}`
                : 'Create an account or sign in to keep chips, stats, and progress connected to your account.'}
            </p>
          </div>
          {session ? (
            <button
              type="button"
              className="casino-page__auth-button casino-page__auth-button--secondary"
              onClick={handleSignOut}
            >
              Sign out
            </button>
          ) : (
            <div className="casino-page__auth-controls">
              <div className="casino-page__auth-tabs" aria-label="Account action">
                <button
                  type="button"
                  className={`casino-page__auth-tab${authMode === 'create' ? ' is-active' : ''}`}
                  onClick={() => handleAuthModeChange('create')}
                >
                  Create account
                </button>
                <button
                  type="button"
                  className={`casino-page__auth-tab${authMode === 'signIn' ? ' is-active' : ''}`}
                  onClick={() => handleAuthModeChange('signIn')}
                >
                  Sign in
                </button>
              </div>
              <form className="casino-page__auth-form" onSubmit={handleAuthSubmit}>
                {authMode === 'create' ? (
                  <>
                    <label className="casino-page__auth-label" htmlFor="casino-auth-display-name">
                      Display name
                    </label>
                    <input
                      id="casino-auth-display-name"
                      className="casino-page__auth-input"
                      type="text"
                      value={displayName}
                      placeholder="Display name"
                      onChange={(event) => setDisplayName(event.target.value)}
                      disabled={!isAuthConfigured}
                    />
                  </>
                ) : null}
                <label className="casino-page__auth-label" htmlFor="casino-auth-email">
                  Email
                </label>
                <input
                  id="casino-auth-email"
                  className="casino-page__auth-input"
                  type="email"
                  value={email}
                  placeholder="you@example.com"
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={!isAuthConfigured}
                />
                <label className="casino-page__auth-label" htmlFor="casino-auth-password">
                  Password
                </label>
                <input
                  id="casino-auth-password"
                  className="casino-page__auth-input"
                  type="password"
                  value={password}
                  placeholder="Password"
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={!isAuthConfigured}
                />
                <button
                  type="submit"
                  className="casino-page__auth-button"
                  disabled={!isAuthConfigured}
                >
                  {authMode === 'create' ? 'Create account' : 'Sign in'}
                </button>
              </form>
            </div>
          )}
          {!isAuthConfigured ? (
            <p className="casino-page__auth-status casino-page__auth-status--error">
              Add your Supabase URL and anon key to enable sign in.
            </p>
          ) : null}
          {authMessage ? <p className="casino-page__auth-status">{authMessage}</p> : null}
          {authError ? (
            <p className="casino-page__auth-status casino-page__auth-status--error">{authError}</p>
          ) : null}
        </section>
        <section className="casino-page__game" aria-label="Casino simulator">
          <iframe
            key={gameReloadKey}
            ref={gameFrameRef}
            className="casino-page__game-frame"
            src={`/casino-simulator/index.html?v=viewport-fix&auth=${gameReloadKey}`}
            title="Casino Cray"
            tabIndex="-1"
            onLoad={handleGameFrameLoad}
          />
        </section>
        <section className="casino-page__collapsible" aria-labelledby="casino-rules-heading">
          <button
            type="button"
            className="casino-page__collapsible-toggle"
            aria-expanded={showRules}
            aria-controls="casino-rules-content"
            onClick={() => setShowRules((current) => !current)}
          >
            <span id="casino-rules-heading">Rules and Controls</span>
            <span
              className={`casino-page__collapsible-arrow${showRules ? ' is-open' : ''}`}
              aria-hidden="true"
            />
          </button>
          {showRules ? (
            <div className="casino-page__collapsible-content" id="casino-rules-content">
              <p className="casino-page__collapsible-intro">
                Learn the basics before stepping onto the casino floor.
              </p>
              <div className="casino-page__rules-grid">
                <article className="casino-page__rules-panel">
                  <h3>Controls</h3>
                  <ul>
                    <li>Move your character with the arrow keys.</li>
                    <li>Press the space bar near games to play them.</li>
                  </ul>
                </article>
                <article className="casino-page__rules-panel">
                  <h3>How to Play</h3>
                  <h4>Blackjack</h4>
                  <ul>
                    <li>Place a bet greater than 0 and press start to start the round.</li>
                    <li>Your goal is to get a higher score than the dealer's without going over 21.</li>
                    <li>The game starts with you receiving two cards and the dealer receiving two cards, one face down.</li>
                    <li>On your turn you can hit to draw another card or stand to keep your score.</li>
                    <li>Face cards are worth 10 and aces can be 1 or 11.</li>
                  </ul>
                  <h4>Roulette</h4>
                  <ul>
                    <li>Select a chip with your bet amount and then press the square you want to bet on.</li>
                    <li>Press start to spin the ball.</li>
                    <li>Red/black/odd/even bet pays 2 to 1.</li>
                    <li>1st 12/2nd 12/3rd 12 pays 3 to 1.</li>
                    <li>Individual numbers pay 36 to 1.</li>
                  </ul>
                  <h4>Slots</h4>
                  <ul>
                    <li>Press spin to start slots and press stop to stop the slots.</li>
                    <li>Three in a row pays 1000.</li>
                    <li>Duplicates give a small bonus even if they are not sequential.</li>
                  </ul>
                </article>
              </div>
            </div>
          ) : null}
        </section>
        <section className="casino-page__collapsible" aria-labelledby="casino-leaderboard-heading">
          <button
            type="button"
            className="casino-page__collapsible-toggle"
            aria-expanded={showLeaderboard}
            aria-controls="casino-leaderboard-content"
            onClick={() => setShowLeaderboard((current) => !current)}
          >
            <span id="casino-leaderboard-heading">Leaderboards</span>
            <span
              className={`casino-page__collapsible-arrow${showLeaderboard ? ' is-open' : ''}`}
              aria-hidden="true"
            />
          </button>
          {showLeaderboard ? (
            <div className="casino-page__collapsible-content" id="casino-leaderboard-content">
              {leaderboardError ? (
                <p className="casino-page__collapsible-intro">{leaderboardError}</p>
              ) : (
                <div className="casino-page__leaderboard-grid">
                  {renderLeaderboard('Current Leaders (all users)', leaderboards.current, 'wallet', '$', '')}
                  {renderLeaderboard('Most Blackjack Games Played (top 3)', leaderboards.blackjack, 'blackjack_games', '', ' games')}
                  {renderLeaderboard('Most Roulette Games Played (top 3)', leaderboards.roulette, 'roulette_games', '', ' games')}
                  {renderLeaderboard('Most Slots Played (top 3)', leaderboards.slots, 'slots_games', '', ' games')}
                  {renderLeaderboard('Highest Value Reached (top 3)', leaderboards.highestWallet, 'highest_wallet', '$', '')}
                </div>
              )}
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
}
